import React from 'react';
import styled from 'styled-components';

const TimePickerContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TimeInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimeSpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const SpinnerButton = styled.button`
  width: 28px;
  height: 24px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: #4285f4;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background: #4285f4;
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TimeDisplay = styled.div`
  width: 50px;
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  background: #ffffff;
  font-family: 'Courier New', monospace;
`;

const Separator = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: #6b7280;
  margin-top: 6px;
`;

interface TimePickerCustomProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const TimePickerCustom: React.FC<TimePickerCustomProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  // Parse the time value (HH:MM format)
  const [hours, setHours] = React.useState<string>(() => {
    if (value && value.includes(':')) {
      return value.split(':')[0];
    }
    return '08';
  });

  const [minutes, setMinutes] = React.useState<string>(() => {
    if (value && value.includes(':')) {
      return value.split(':')[1];
    }
    return '00';
  });

  // Sincronizar com o value prop
  React.useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      setHours(h.padStart(2, '0'));
      setMinutes(m.padStart(2, '0'));
    }
  }, [value]);

  // Chamar onChange quando hours ou minutes mudam
  React.useEffect(() => {
    const newTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    if (newTime !== value) {
      onChange(newTime);
    }
  }, [hours, minutes, onChange, value]);

  const incrementHours = () => {
    const h = parseInt(hours, 10);
    if (h < 23) {
      setHours(String(h + 1).padStart(2, '0'));
    }
  };

  const decrementHours = () => {
    const h = parseInt(hours, 10);
    if (h > 0) {
      setHours(String(h - 1).padStart(2, '0'));
    }
  };

  const incrementMinutes = () => {
    const m = parseInt(minutes, 10);
    if (m < 59) {
      setMinutes(String(m + 1).padStart(2, '0'));
    }
  };

  const decrementMinutes = () => {
    const m = parseInt(minutes, 10);
    if (m > 0) {
      setMinutes(String(m - 1).padStart(2, '0'));
    }
  };

  return (
    <TimePickerContainer>
      <TimeInputWrapper>
        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
          Hora
        </label>
        <TimeSpinnerContainer>
          <SpinnerButton
            type="button"
            onClick={incrementHours}
            disabled={disabled || parseInt(hours, 10) >= 23}
            title="Aumentar hora"
          >
            ▲
          </SpinnerButton>
          <TimeDisplay>{hours}</TimeDisplay>
          <SpinnerButton
            type="button"
            onClick={decrementHours}
            disabled={disabled || parseInt(hours, 10) <= 0}
            title="Diminuir hora"
          >
            ▼
          </SpinnerButton>
        </TimeSpinnerContainer>
      </TimeInputWrapper>

      <Separator>:</Separator>

      <TimeInputWrapper>
        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
          Min
        </label>
        <TimeSpinnerContainer>
          <SpinnerButton
            type="button"
            onClick={incrementMinutes}
            disabled={disabled || parseInt(minutes, 10) >= 59}
            title="Aumentar minuto"
          >
            ▲
          </SpinnerButton>
          <TimeDisplay>{minutes}</TimeDisplay>
          <SpinnerButton
            type="button"
            onClick={decrementMinutes}
            disabled={disabled || parseInt(minutes, 10) <= 0}
            title="Diminuir minuto"
          >
            ▼
          </SpinnerButton>
        </TimeSpinnerContainer>
      </TimeInputWrapper>
    </TimePickerContainer>
  );
};
